// src/pages/Appli/RDVAgenda/CreateRDVAgendaModal.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes, FaTrash } from "react-icons/fa";
import { addRDV, updateRDV } from "../../../services/rdvAgendaServices";

/* =========================
   Helpers
   ========================= */
const pad2 = (n) => String(n).padStart(2, "0");

const hhmm = (t) => (t ? String(t).slice(0, 5) : "");

const toHHMMSS = (t) => {
  if (!t) return "";
  const s = String(t);
  return s.length === 5 ? `${s}:00` : s;
};

const minutesOf = (hhmmStr) => {
  if (!hhmmStr) return 0;
  const [h, m] = hhmmStr.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
};

const isReservedRDV = (rdv) =>
  !!(rdv?.nom_client && String(rdv.nom_client).trim().length > 0);

const blankForm = () => ({
  date_rdv: "",
  heure_debut: "09:00",
  heure_fin: "09:30",
  lieu: "",
  note_client: "",
  // client (si réservé)
  nom_client: "",
  prenom_client: "",
  tel_client: "",
});

export default function CreateRDVAgendaModal({
  isOpen,
  onClose,
  refreshList,
  rdvToEdit,
  preset,
  onDelete, // optionnel : callback suppression (souvent géré par la page)
}) {
  const isEdit = !!rdvToEdit?.id;

  const [form, setForm] = useState(blankForm());
  const [reserved, setReserved] = useState(false);
  const [saving, setSaving] = useState(false);

  // garde-fou pour éviter setState après unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // lock scroll quand modale ouverte
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // fermeture via ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // IMPORTANT : on initialise le form uniquement quand on ouvre / change de cible
  // ⚠️ aucun refreshList() ici => pas de boucle
  useEffect(() => {
    if (!isOpen) return;

    const base = blankForm();
    const src = rdvToEdit || preset || null;

    if (src) {
      setForm({
        date_rdv: src.date_rdv || "",
        heure_debut: hhmm(src.heure_debut) || base.heure_debut,
        heure_fin: hhmm(src.heure_fin) || base.heure_fin,
        lieu: src.lieu || "",
        note_client: src.note_client || "",
        nom_client: src.nom_client || "",
        prenom_client: src.prenom_client || "",
        tel_client: src.tel_client || "",
      });
      setReserved(isReservedRDV(src));
    } else {
      // création pure
      setForm(base);
      setReserved(false);
    }

    setSaving(false);
  }, [isOpen, rdvToEdit, preset]);

  const title = useMemo(() => {
    if (isEdit) return "Modifier un RDV";
    if (preset) return "Dupliquer / Créer un RDV";
    return "Créer un RDV";
  }, [isEdit, preset]);

  const onOverlayClick = (e) => {
    // clic en dehors de la modale => fermer
    if (e.target === e.currentTarget) onClose?.();
  };

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    if (!form.date_rdv) return "La date est obligatoire.";
    if (!form.heure_debut) return "L'heure de début est obligatoire.";
    if (!form.heure_fin) return "L'heure de fin est obligatoire.";

    const start = minutesOf(form.heure_debut);
    const end = minutesOf(form.heure_fin);
    if (end <= start) return "L'heure de fin doit être après l'heure de début.";

    if (reserved) {
      const hasName = String(form.nom_client || "").trim().length > 0;
      if (!hasName) return "Nom client obligatoire si RDV réservé.";
    }

    return null;
  };

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (saving) return;

      const err = validate();
      if (err) {
        toast.error(err);
        return;
      }

      // payload final
      const payload = {
        date_rdv: form.date_rdv,
        heure_debut: toHHMMSS(form.heure_debut),
        heure_fin: toHHMMSS(form.heure_fin),
        lieu: form.lieu || "",
        note_client: form.note_client || "",
        nom_client: reserved ? form.nom_client || "" : "",
        prenom_client: reserved ? form.prenom_client || "" : "",
        tel_client: reserved ? form.tel_client || "" : "",
      };

      setSaving(true);

      try {
        if (isEdit) {
          // ⚠️ nécessite updateRDV(id, payload) dans ton service
          await updateRDV({ id: rdvToEdit.id, ...payload });
          toast.success("RDV modifié ✅");
        } else {
          await addRDV(payload);
          toast.success("RDV créé ✅");
        }

        // refresh uniquement APRES succès (pas dans useEffect)
        await refreshList?.();

        if (mountedRef.current) {
          setSaving(false);
          onClose?.();
        }
      } catch (error) {
        console.error("Erreur submit RDV :", error);
        toast.error("Erreur lors de l'enregistrement.");
        if (mountedRef.current) setSaving(false);
      }
    },
    [saving, reserved, form, isEdit, rdvToEdit, refreshList, onClose],
  );

  const handleDeleteClick = useCallback(async () => {
    if (!onDelete) return;
    if (!window.confirm("Supprimer ce RDV ?")) return;
    try {
      await onDelete();
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  }, [onDelete, onClose]);

  const handleClearClient = useCallback(async () => {
    if (!isEdit || !rdvToEdit?.id) return;
    if (!reserved) return;

    if (
      !window.confirm(
        "Libérer ce créneau ? (les infos client seront supprimées)",
      )
    )
      return;

    setSaving(true);
    try {
      const payload = {
        date_rdv: form.date_rdv,
        heure_debut: toHHMMSS(form.heure_debut),
        heure_fin: toHHMMSS(form.heure_fin),
        lieu: form.lieu || "",
        note_client: form.note_client || "",
        nom_client: "",
        prenom_client: "",
        tel_client: "",
      };

      await updateRDV({
        id: rdvToEdit.id,
        ...payload,
        nom_client: "",
        prenom_client: "",
        tel_client: "",
        lieu: "",
        note_client: "",
      });

      toast.success("Créneau libéré ✅");

      // Mets à jour l’UI locale
      if (mountedRef.current) {
        setReserved(false);
        setForm((s) => ({
          ...s,
          nom_client: "",
          prenom_client: "",
          tel_client: "",
        }));
      }

      await refreshList?.();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la libération du créneau.");
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [isEdit, rdvToEdit, reserved, form, refreshList]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onMouseDown={onOverlayClick}
    >
      <div className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-white/10">
        {/* Header */}
        <div className="bg-primary text-white px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="font-extrabold uppercase">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-xl bg-white/15 hover:bg-white/20"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Date + heures */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Date
              </label>
              <input
                type="date"
                value={form.date_rdv}
                onChange={(e) => setField("date_rdv", e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Début
              </label>
              <input
                type="time"
                value={form.heure_debut}
                onChange={(e) => setField("heure_debut", e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Fin
              </label>
              <input
                type="time"
                value={form.heure_fin}
                onChange={(e) => setField("heure_fin", e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Lieu + note */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Lieu (optionnel)
              </label>
              <input
                type="text"
                value={form.lieu}
                onChange={(e) => setField("lieu", e.target.value)}
                placeholder="Ex: Cabinet / Dojo / Visio..."
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Note (optionnel)
              </label>
              <input
                type="text"
                value={form.note_client}
                onChange={(e) => setField("note_client", e.target.value)}
                placeholder="Ex: Apporter documents..."
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Disponible / Réservé */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/40">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-bold text-gray-800 dark:text-gray-100">
                Statut du créneau
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={reserved}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setReserved(v);
                    if (!v) {
                      // si on repasse "disponible", on vide les infos client
                      setForm((s) => ({
                        ...s,
                        nom_client: "",
                        prenom_client: "",
                        tel_client: "",
                      }));
                    }
                  }}
                />
                RDV réservé (sinon créneau disponible)
              </label>
            </div>

            {reserved && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    Nom *
                  </label>
                  <input
                    type="text"
                    value={form.nom_client}
                    onChange={(e) => setField("nom_client", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={form.prenom_client}
                    onChange={(e) => setField("prenom_client", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    Téléphone
                  </label>
                  <input
                    type="text"
                    value={form.tel_client}
                    onChange={(e) => setField("tel_client", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              {isEdit && reserved ? (
                <Button
                  text="Libérer le créneau"
                  icon={FaTimes}
                  type="button"
                  className="btn-warning gap-2"
                  onClick={handleClearClient}
                  disabled={saving}
                />
              ) : null}
            </div>

            <div>
              {isEdit && onDelete ? (
                <Button
                  text="Supprimer"
                  icon={FaTrash}
                  type="button"
                  className="btn-danger gap-2"
                  onClick={handleDeleteClick}
                  disabled={saving}
                />
              ) : null}
            </div>

            <div className="flex gap-2">
              <Button
                text="Annuler"
                icon={FaTimes}
                type="button"
                className="btn-ghost gap-2"
                onClick={onClose}
                disabled={saving}
              />
              <Button
                text={saving ? "Enregistrement..." : "Enregistrer"}
                icon={FaSave}
                type="submit"
                className="btn-primary gap-2"
                disabled={saving}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
